/**
 * Account Details Component
 * Displays detailed information about a specific trade account
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Flag from "react-world-flags";
import {
  Typography,
} from "@mui/material";
import {
  LinkOff,
} from "@mui/icons-material";
import { services } from "../../../services/api";
import { Breadcrumb, Tabs, Icon } from "../../../components/ds";
import { accountStatusOf, sessionStatusOf } from "../accountStatus";
import LoadingSpinner from "../Components/LoadingSpinner";
import AlertMessage from "../Components/AlertMessage";
import SessionTimer from "../Components/SessionTimer";
import LoginModal from "../Components/LoginModal";
import BrokerPlatformDetails from "./Tabs/BrokerPlatformDetails";
import AccountSettings from "./Tabs/AccountSettings";
import FundDetails from "./Tabs/FundDetails";
import CustomerDetails from "./Tabs/CustomerDetails";

// Tab labels — kept in sync with the <Tab> list below; used for the breadcrumb's
// current-location segment so the trail reads "Broker Accounts › Name › Settings".
//
// "Activity Log" was removed. Its timeline was hardcoded mock data, and once that
// went the tab held exactly two fields: session_status — already shown in the
// overview card header above, on EVERY tab — and last_login_at, which now sits in
// that same card's detail grid. A whole tab for one duplicated status and one date.
// The card header also carries the live expiry countdown (SessionTimer), so nothing
// was lost. If real activity history ever lands, it needs its own endpoint first.
const TAB_LABELS = [
  "Broker Platform Details",
  "Fund Details",
  "Customer Details",
  "Settings",
];

const AccountDetails = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();

  // Component state
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [rateLimitUsage, setRateLimitUsage] = useState(null);

  // Login modal state
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Load account details
  const loadAccountDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch account details
      const response = await services.brokerAccounts.getAccount(accountId);
      let responseData = response.data || {};
      if (responseData.status === "success") {
        setAccount(responseData.data);
      } else {
        setError(
          `Failed to load accounts: ${
            responseData.error ||
            "Unable to retrieve account details from backend API"
          }`
        );
      }
      // Fetch rate limit usage metrics after account is loaded
      if (accountId) {
        try {
          const usageResp = await services.brokerAccounts.getRateLimitUsage(accountId);
          const usageData = usageResp.data || {};
          if (usageData.status === "success") {
            setRateLimitUsage(usageData.data);
          } else {
            setRateLimitUsage(null);
          }
        } catch (usageErr) {
          setRateLimitUsage(null);
        }
      }
    } catch (err) {
      setError(`Failed to load accounts: ${err.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (accountId) {
      loadAccountDetails();
    }
  }, [accountId, loadAccountDetails]);

  // getStatusColor was deleted: its only two references were its own definition and
  // a `getStatusColor={...}` prop on <BrokerPlatformDetails>, which that component
  // never destructured — see its signature: ({ account, rateLimitUsage }).

  const getStatusTextColor = (status) => {
    switch (status?.toLowerCase()) {
      case "verified":
      case "active":
        return "text-profit";
      case "inactive":
        return "text-fg-2";
      case "error":
        return "text-loss";
      case "pending":
        return "text-warning";
      default:
        return "text-fg-2";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "verified":
      case "active":
        return <Icon name="success" className="text-profit" size={18} />;
      case "inactive":
        return <Icon name="error" className="text-fg-2" size={18} />;
      case "error":
        return <Icon name="error" className="text-loss" size={18} />;
      case "pending":
        return <Icon name="pending" className="text-warning" size={18} />;
      default:
        return <Icon name="error" className="text-fg-2" size={18} />;
    }
  };

  // Takes the DERIVED session (sessionStatusOf), never the raw session_status —
  // that field says "active" whenever a session row exists, even a lapsed one, so
  // keying the icon off it drew a green tick next to the words "Session expired".
  const getSessionStatusIcon = (session) => {
    if (session.isActive) return <Icon name="success" size={18} />;
    if (session.hasSession) return <Icon name="pending" size={18} />;
    return <LinkOff fontSize="small" />;
  };


  // Handle login modal
  const handleOpenLoginModal = () => {
    // Clear any existing broker login status data to prevent auto-processing
    localStorage.removeItem('brokerLoginStatus');
    localStorage.removeItem('brokerSessionStatus');
    sessionStorage.removeItem('brokerLoginStatus');
    setLoginModalOpen(true);
  };

  const handleCloseLoginModal = () => {
    setLoginModalOpen(false);
  };

  const handleLoginSuccess = (loginDetails) => {
    console.log("Login successful for account:", accountId, loginDetails);
    // Refresh account details to get updated session information
    loadAccountDetails();
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this broker account? This action cannot be undone."
      )
    ) {
      try {
        setLoading(true);
        await services.brokerAccounts.deleteAccount(accountId);
        
        // Navigate back to accounts list after successful deletion
        navigate("/TradeAccounts");
      } catch (error) {
        console.error("Failed to delete broker account:", error);
        setError("Failed to delete broker account. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-1 py-4 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <LoadingSpinner message="Loading account details..." />
        </div>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="min-h-screen bg-bg-1 py-4 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <AlertMessage
            severity="error"
            title="Error"
            message={error || "Account not found"}
          />
          <button
            onClick={() => navigate("/TradeAccounts")}
            className="btn btn-secondary mt-2"
          >
            <Icon name="back" size={16} />
            Back to Accounts
          </button>
        </div>
      </div>
    );
  }

  // Both read through the ONE mapping shared with the list card (../accountStatus),
  // so a given account can no longer say "Inactive" on the list and "ACTIVE" here.
  const accountStatus = accountStatusOf(account);
  const session = sessionStatusOf(account);
  // A lapsed session should read red on this card. sessionStatusOf returns "warn"
  // (amber) for it — kept as-is because that helper is shared with LoginModal and
  // BrokerPlatformDetails — so the red is applied locally: had a session, not live.
  const sessionPillVariant =
    session.hasSession && !session.isActive ? "loss" : session.variant;

  return (
    <div className="min-h-screen bg-bg-1 pt-2 pb-6 px-2 sm:px-4 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb — replaces the ad-hoc back button (shows location, works on mobile) */}
        <Breadcrumb
          className="mb-2"
          items={[
            { label: "Broker Accounts", to: "/TradeAccounts" },
            { label: account.account_holder_name || "Account" },
            { label: TAB_LABELS[activeTab] || "Details" },
          ]}
        />
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Typography
                variant="h4"
                component="h1"
                className="text-fg-1"
                sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
              >
                {account.account_holder_name || "Unknown Account"}
              </Typography>
            </div>
          </div>
          {/* w-full + flex-wrap: these four buttons total ~500px and `.btn` is
              white-space:nowrap, so they cannot shrink. Unconstrained they ran off a
              343px screen — Delete was clipped and Connect (the primary action on
              this page) was entirely off the right edge, taking the whole page into
              horizontal scroll. w-full gives the row something to wrap against;
              sm:w-auto keeps desktop as a single inline row. */}
          {/* ONE action here, deliberately. A broker session must be re-established
              every market day, so Connect is the only FREQUENT thing on this screen
              and it earns the header slot — reachable from every tab.
              · Edit and Delete moved to the Settings tab: rare, and they were
                duplicated there anyway.
              · Refresh is gone: it called getAccount(), which fetches the same record
                AuthContext already keeps live over the broker-session WS topic (see
                TradeAccounts.jsx). A button to re-fetch live data is a symptom, not a
                feature. */}
          {/* Paper accounts get no Connect at all — there is no broker session to
              establish, and the papertrade adapter has no login endpoint to call. */}
          {session.needsLogin && (
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <button
                onClick={handleOpenLoginModal}
                className={`btn ${session.isActive ? "btn-secondary" : "btn-primary"} w-full sm:w-auto`}
              >
                {session.isActive ? <Icon name="refresh" size={16} /> : <Icon name="link" size={16} />}
                {session.isActive ? "Refresh Session" : "Connect"}
              </button>
            </div>
          )}
        </div>

        {/* Account Overview Card - Clean & Simplified */}
        <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] mb-6">
          {/* Header Section - With Timer Integration */}
          <div className="px-4 sm:px-6 py-4 border-b border-[var(--divider)]">
            {/* flex-wrap + min-w-0: the account-ID caption is one long unbreakable
                mono token, so it held the left side open and squeezed the status
                chips until their labels ("VERIFIED") wrapped a syllable per line. */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <Icon name="bank" className="text-accent shrink-0" size={28} />
                <div className="min-w-0">
                  <Typography
                    variant="h6"
                    component="h2"
                    className="text-fg-1 font-medium mb-1"
                  >
                    {account.broker_display_name || "Unknown Broker"}
                  </Typography>
                  <Typography
                    variant="caption"
                    className="text-fg-2 font-mono text-xs block break-all"
                    sx={{ fontSize: '0.7rem' }}
                  >
                    DeeTrade Account ID: {account.account_id}
                  </Typography>
                </div>
              </div>
              {/* Two bare pills reading "ACTIVE" and "INACTIVE" used to sit here. They
                  were showing DIFFERENT axes — account.status and session_status — in
                  identical chips with no label, so they read as a contradiction. Each
                  pill now states which fact it is reporting. See accountStatus.js for
                  why account.status can only ever express ONE of verified/enabled. */}
              <div className="text-right shrink-0">
                <div className="flex items-center justify-end flex-wrap gap-2 mb-2">
                  <span className={`ds-badge sm ${accountStatus.variant}`}>
                    {getStatusIcon(account.status)}
                    {accountStatus.label}
                  </span>

                  {/* Session pill — SAME treatment at every width. The live countdown
                      reads INSIDE the pill ("Session active · 3h 18m"), coloured by
                      time-left and red on expiry (chip-only's getSessionStatus path,
                      not the buggy getTimeBasedColor one). This replaced a phone-only
                      pill + a desktop "compact" time-and-stub-bar lane: one treatment
                      everywhere reads cleaner and is what was asked for.
                      `sessionPillVariant` forces red on a LAPSED session — sessionStatusOf
                      returns "warn" (amber) there; that helper is shared with LoginModal
                      and BrokerPlatformDetails, so the red-on-expiry choice is kept local
                      to this card rather than changed for all three. */}
                  {session.expiresAt ? (
                    <SessionTimer
                      sessionExpiresAt={session.expiresAt}
                      variant="chip-only"
                      size="small"
                      showIcon
                      label={session.label}
                    />
                  ) : (
                    <span className={`ds-badge sm ${sessionPillVariant}`}>
                      {getSessionStatusIcon(session)}
                      {session.label}
                    </span>
                  )}
                </div>
                {/* Thin live progress bar under the pills at every width — the
                    draining-session indicator. The time is in the pill above; this is
                    just the "session is alive and running down" signal, without the
                    boxed lane that felt disconnected. */}
                {session.expiresAt && (
                  <div className="mt-2">
                    <SessionTimer
                      sessionExpiresAt={session.expiresAt}
                      variant="progress"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Minimal Account Details - Single Section */}
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <Typography variant="body2" className="text-fg-2 mb-1">
                  Account Holder
                </Typography>
                <Typography variant="body1" className="font-medium text-fg-1">
                  {account.account_holder_name || "Unknown"}
                </Typography>
              </div>
              
              {account.broker_currency && (
                <div className="text-center">
                  <Typography variant="body2" className="text-fg-2 mb-1">
                    Currency
                  </Typography>
                  <Typography variant="body1" className="font-medium text-fg-1">
                    {account.broker_currency}
                  </Typography>
                </div>
              )}
              
              {account.broker_country_code && (
                <div className="text-center">
                  <Typography variant="body2" className="text-fg-2 mb-1">
                    Region
                  </Typography>
                  <div className="flex items-center justify-center gap-2">
                    {/* Bundled flag, not a flagcdn.com hotlink — see TradeAccounts.jsx */}
                    <Flag
                      code={account.broker_country_code}
                      className="w-5 h-4 rounded-sm object-cover"
                      title={account.broker_country_code}
                      fallback={null}
                    />
                    <Typography variant="body1" className="font-medium text-fg-1">
                      {account.broker_country_code.toUpperCase()}
                    </Typography>
                  </div>
                </div>
              )}

              <div className="text-center">
                <Typography variant="body2" className="text-fg-2 mb-1">
                  Created
                </Typography>
                <Typography variant="body1" className="font-medium text-fg-1">
                  {new Date(account.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </Typography>
              </div>

              {/* NO "Last Login" row here. I added one when the Activity Log tab was
                  removed, believing last_login_at was that tab's one real field. It
                  is not a field at all: broker_accounts has no last_login_at column
                  (models/broker.py), and BrokerAccountResponse never returns one —
                  `last_login` exists only on the separate BrokerAccountStatus schema
                  (schemas/broker_account.py:129), which this endpoint doesn't use.
                  So it read `undefined` and rendered a permanent "Never" for every
                  account. The Activity Log was therefore fiction end to end: a mock
                  timeline, a duplicated status, and a phantom date. */}
            </div>
          </div>
        </div>

        {/* Tabs for different sections — DS pill tabs (see components/ds/Tabs) */}
        <Tabs
          className="mb-6"
          value={activeTab}
          onChange={setActiveTab}
          tabs={TAB_LABELS.map((label) => ({ label }))}
        />

        {/* Tab Content */}
        {/* getStatusColor / getSessionStatusIcon used to be passed to
            BrokerPlatformDetails, but it never destructured either — its signature is
            ({ account, rateLimitUsage }). */}
        {activeTab === 0 && (
          <BrokerPlatformDetails
            account={account}
            rateLimitUsage={rateLimitUsage}
          />
        )}

        {activeTab === 1 && (
          <FundDetails 
            account={account}
          />
        )}

        {activeTab === 2 && (
          <CustomerDetails 
            account={account}
          />
        )}

        {activeTab === 3 && (
          <AccountSettings 
            account={account}
            onEditAccount={() => navigate(`/AddTradeAccount/${accountId}`)}
            onDeleteAccount={handleDeleteAccount}
            onConnectAccount={handleOpenLoginModal}
          />
        )}

        {/* Login Modal */}
        <LoginModal
          open={loginModalOpen}
          onClose={handleCloseLoginModal}
          account={account}
          onLoginSuccess={handleLoginSuccess}
          isReconnect={true}
          title={session.isActive ? "Refresh Session" : "Connect Account"}
        />
      </div>
    </div>
  );
};

export default AccountDetails;
